import React, { type ComponentPropsWithoutRef, forwardRef, type ReactNode } from 'react';

import { Flex } from '../Flex';
import type { IconComponent } from '../Icon/createIcon';
import type { IconIntent, IconSize } from '../Icon/types';

import { Styled } from './styles';
import type { ButtonSize, ButtonVariant } from './types';

export { buttonSizes, buttonVariants } from './types';
export type { ButtonSize, ButtonVariant } from './types';

type ButtonProps = {
  variant?: ButtonVariant;
  icon?: IconComponent;
  iconPlacement?: 'left' | 'right';
  iconIntent?: IconIntent;
  size?: ButtonSize;
  fullWidth?: boolean;
  active?: boolean;
};
type Props = Omit<ComponentPropsWithoutRef<'button'>, keyof ButtonProps> & ButtonProps;

const hasIconWithoutText = (icon?: IconComponent, children?: ReactNode): boolean => !!(icon && !children);
const iconSizeByButtonSize: Record<ButtonSize, IconSize> = {
  s: 's',
  m: 'm',
  l: 'm',
  xl: 'l',
};

export const Button = forwardRef<HTMLButtonElement, Props>(
  (
    {
      iconPlacement = 'left',
      iconIntent,
      variant = 'primary',
      type = 'button',
      size = 'm',
      fullWidth = false,
      icon: IconEl,
      children,
      active,
      ...props
    },
    ref,
  ) => (
    <Styled.Button
      {...props}
      ref={ref}
      variant={variant}
      type={type}
      size={size}
      fullWidth={fullWidth}
      hasIconWithoutText={hasIconWithoutText(IconEl, children)}
      active={active}
    >
      <Flex.Row space="s" justifyContent="center">
        {IconEl && iconPlacement === 'left' && <IconEl size={iconSizeByButtonSize[size]} intent={iconIntent} />}
        {children && <span>{children}</span>}
        {IconEl && iconPlacement === 'right' && <IconEl size={iconSizeByButtonSize[size]} intent={iconIntent} />}
      </Flex.Row>
    </Styled.Button>
  ),
);

Button.displayName = 'Button';
